import dataHelpers from '@/helpers/dataHelpers'
import positionHelpers from '@/helpers/positionHelpers'
import { DomainModelRole } from '@/constants/cardTypes'

export const svgGraphData = {
  namespaced: true,
  state: {},
  getters: {
    baseData: (state, getters, rootState) => {
      const columns = []
      const columnsWithCards = []
      const laneHeights = {}
      const events = rootState.workflow.workflow.domainEvents || []
      const showReleaseNo = rootState.workflow.showReleaseNo
      const showTagCardType = rootState.workflow.showTagCardType

      // Find Tag card type
      const tagCardTypes = (rootState.cardTypes.cardTypes || []).filter((ct) => ct.domainModelRole === DomainModelRole.TAG)
      const tagCardTypeId = tagCardTypes[0]?.id || null

      const hasFilters = (showReleaseNo && showReleaseNo.length > 0) ||
        (showTagCardType && showTagCardType.length > 0)

      const filteredEvents = hasFilters
        ? filterCards(events, showReleaseNo, rootState.cardTypes.cardTypes, showTagCardType, tagCardTypeId)
        : events
      const parentChildEventMap = dataHelpers.createMainParentChildEventMap(filteredEvents)

      createColumnsMap(parentChildEventMap, getters.svgGroupsData, 'start', 0, columns, laneHeights, columnsWithCards)

      for (const column of columns) {
        for (const lane in column) {
          laneHeights[lane] = Math.max(laneHeights[lane], column[lane].length)
        }
      }

      const cardIdToEventIdMap = {}
      for (const event of filteredEvents) {
        if (!event.cards) continue

        for (const req of event.cards) {
          cardIdToEventIdMap[req.id] = event.id
        }
      }

      return {
        columns,
        laneHeights,
        columnsWithCards,
        cardIdToEventIdMap,
        parentChildEventMap
      }
    },
    svgGroupsData: (state, getters, rootState) => {
      const svgGroupsData = {}
      if (rootState.workflow.workflow.groups) {
        let prevGroup = null
        const sortedGroups = JSON.parse(JSON.stringify(rootState.workflow.workflow.groups))
        sortedGroups.sort((a, b) => a.startSlotX - b.startSlotX)
        let index = 0
        for (const group of sortedGroups) {
          svgGroupsData[group.id] = {
            id: group.id,
            name: group.name,
            startSlotX: group.startSlotX,
            x: positionHelpers.getGroupXPointFromSlot(group.startSlotX),
            width: positionHelpers.getGroupXPointFromSlot(1),
            index
          }
          if (prevGroup) {
            prevGroup.width = positionHelpers.getGroupXPointFromSlot(group.startSlotX) - prevGroup.x
          }
          prevGroup = svgGroupsData[group.id]
          index++
        }

        if (prevGroup) {
          prevGroup.width = -1
        }
      }

      return svgGroupsData
    },
    svgLanesData: (state, getters, rootState) => {
      const svgLanesData = {}
      let yPosLanes = 0
      let laneAbove = null
      if (rootState.workflow.workflow.lanes) {
        for (const [index, lane] of rootState.workflow.workflow.lanes.entries()) {
          const height = 156 * (getters.baseData.laneHeights[lane.id] || 1)
          svgLanesData[lane.id] = {
            id: lane.id,
            x: 0,
            y: svgLanesData[laneAbove] ? svgLanesData[laneAbove].y + svgLanesData[laneAbove].height : 0,
            height,
            name: lane.name,
            laneAbove,
            emptyLane: !getters.baseData.laneHeights[lane.id],
            index
          }
          if (laneAbove) {
            svgLanesData[laneAbove].laneBelow = lane.id
          }
          laneAbove = lane.id
          yPosLanes = yPosLanes + height
        }
        if (svgLanesData[laneAbove]) {
          svgLanesData[laneAbove].height += 15 // add height to the last lane
        }
      }
      return svgLanesData
    },
    svgGraphData: (state, getters, rootState) => {
      let events, i
      const svgGraphData = {}
      const hasDirectGatewayParent = (parents, currentX) => {
        if (!Array.isArray(parents) || !parents.length) {
          return false
        }
        const parentId = parents.find((parent) => svgGraphData[parent] && svgGraphData[parent].type === 'bpmn:ExclusiveGateway')
        if (parentId) {
          if ((currentX - svgGraphData[parentId]?.x) === 160) {
            return true
          }
        }
        return false
      }
      const showReleaseNo = rootState.workflow.showReleaseNo
      const showTagCardType = rootState.workflow.showTagCardType
      const hasFilters = (showReleaseNo && showReleaseNo.length > 0) ||
        (showTagCardType && showTagCardType.length > 0)
      for (let column = 0; column < getters.baseData.columns.length; column++) {
        for (const role in getters.baseData.columns[column]) {
          events = getters.baseData.columns[column][role]

          const offsetList = []
          const detectDuplicates = new Set()
          if (events.length > 1) {
            for (i = 0; i < events.length; i++) {
              // create an offsetList [0, 1, 2, 3, 4, etc ] and remove the offsets that are used
              // then use the remaining numbers for nodes without offset
              offsetList.push(i)

              // set duplicate offsets to null
              if (detectDuplicates.has(events[i].offset)) {
                events[i] = JSON.parse(JSON.stringify(events[i]))
                events[i].offset = null
              } else {
                detectDuplicates.add(events[i].offset)
              }
            }
            // sort on offset and put null last
            events.sort((a, b) => { return (a.offset === null) - (b.offset === null) || +(a.offset > b.offset) || -(a.offset < b.offset) })
          }
          for (i = 0; i < (events.length); i++) {
            const offset = (events[i].offset > 0 || events[i].offset === 0) ? events[i].offset : offsetList.length === 0 ? events[i].offset : offsetList.shift()
            // const x = events[i].parents.find(id => id === 'start') ? 80 : svgGraphData[events[i].parents[0]].x + 160
            const x = 80 + 160 * column
            svgGraphData[events[i].id] = {
              id: events[i].id,
              parents: events[i].parents,
              x,
              y: getters.svgLanesData[events[i].laneId].y + 30 + offset * 156,
              type: events[i].type,
              margin: events[i].type === 'bpmn:ExclusiveGateway' ? 33 : 0,
              name: events[i].description,
              laneId: events[i].laneId,
              groupId: events[i].groupId,
              outlined: events[i].cards?.length === 0 && hasFilters,
              conditionLabel: events[i].conditionLabel || undefined,
              conditionLabels: events[i].conditionLabels || undefined,
              showConditionLabelOnEvent: hasDirectGatewayParent(events[i].parents, x),
              offset,
              childIds: svgGraphData[events[i].id] ? svgGraphData[events[i].id].childIds : undefined,
              cards: events[i].cards?.filter(req => (!showReleaseNo || (showReleaseNo.length === 0) || showReleaseNo.includes(req.releaseNo))) || [],
              subprocess: events[i].subprocess,
              color: events[i].color,
              version: events[i].version
            }
            const offsetIndex = offsetList.findIndex(offset => offset === events[i].offset)
            if (offsetIndex >= 0) {
              offsetList.splice(offsetIndex, 1)
            }
            if (svgGraphData[events[i].id].y < 0) { svgGraphData[events[i].id].y = 0 } // bug fix for when nodes jumps to minus coordinades because of unwanted collisions between svg elements
            // add child
            for (let j = 0; j < events[i].parents.length; j++) {
              if (events[i].parents[j] !== 'start') {
                if (!svgGraphData[events[i].parents[j]] || !svgGraphData[events[i].parents[j]].childIds) {
                  if (!svgGraphData[events[i].parents[j]]) {
                    svgGraphData[events[i].parents[j]] = {}
                  }
                  svgGraphData[events[i].parents[j]].childIds = []
                }
                svgGraphData[events[i].parents[j]].childIds.push(events[i].id)
              }
            }
          }
        }
      }
      return svgGraphData
    }
  }
}

// build column by column
function createColumnsMap (parentChildEventMap, groups, parentId, column, columns, laneHeights, columnsWithCards) {
  for (const event of parentChildEventMap[parentId]) {
    let thisColumnIndex = column
    if (event.groupId && groups[event.groupId]?.startSlotX > 0) {
      thisColumnIndex = groups[event.groupId].startSlotX
    }

    // since the function is called recursively, the column might already exist, in that case add to it, otherwise initiate
    let thisColumn, thisColumnWithCards
    if (columns[thisColumnIndex]) {
      thisColumn = columns[thisColumnIndex]
    } else {
      thisColumn = []
      columns[thisColumnIndex] = thisColumn
    }
    if (columnsWithCards[thisColumnIndex]) {
      thisColumnWithCards = columnsWithCards[thisColumnIndex]
    } else {
      thisColumnWithCards = {}
      // commented the next line because it seems like a bug
      // thisColumnWithCards[thisColumnIndex] = thisColumn
    }

    // ******
    // EVENTS
    // ******
    // get lane name
    const laneId = event.laneId
    // init array if emtpy
    if (!thisColumn[laneId]) { thisColumn[laneId] = [] }
    // add to matrix
    thisColumn[laneId].push(event)

    // ******
    // CARDS
    // ******
    // get lane name
    for (const card of event.cards || []) {
      const release = card.releaseNo || 'unassigned'
      // init array if emtpy
      if (!thisColumnWithCards[release]) { thisColumnWithCards[release] = [] }
      // add to matrix
      thisColumnWithCards[release].push(card)
      columnsWithCards[thisColumnIndex] = thisColumnWithCards
    }

    // calculation for figuring out the height of this swim lane
    const currentCellHeight = (event.offset + 1) > (thisColumn[laneId].length - 1) ? event.offset + 1 : thisColumn[laneId].length - 1
    if (!laneHeights[laneId] || laneHeights[laneId] < currentCellHeight) {
      laneHeights[laneId] = currentCellHeight
    }

    if (parentChildEventMap[event.id]) {
      // add next column to this.columns
      createColumnsMap(parentChildEventMap, groups, event.id, thisColumnIndex + 1, columns, laneHeights, columnsWithCards)
    }
  }
}

function getTagsForCard (card) {
  // Get tags directly from the card object (stored in backend)
  return card.tags || []
}

function filterCards (events, showReleaseNo, cardTypes, showTagCardType, tagCardTypeId) {
  if (!events?.length) {
    return []
  }

  const typeMap = new Map((cardTypes ?? []).map((type) => [type.id, type.showInBacklog]))

  const releaseSet = (showReleaseNo && showReleaseNo.length > 0) ? new Set(showReleaseNo) : null
  const tagCardTypeSet = showTagCardType && showTagCardType.length > 0 ? new Set(showTagCardType) : null

  // Keep all events, but filter their cards based on filters
  const hasTagFilter = tagCardTypeSet && tagCardTypeId

  return events.map((event) => {
    let filteredCards = event.cards ?? []

    // Filter by release (original logic - supports multi-select)
    // Release filter always considers showInBacklog
    if (releaseSet) {
      filteredCards = filteredCards.filter((req) => releaseSet.has(req.releaseNo) && typeMap.get(req.cardTypeId))
    } else if (!hasTagFilter) {
      // If no release filter AND no tag filter, filter by showInBacklog (default behavior)
      filteredCards = filteredCards.filter((req) => typeMap.get(req.cardTypeId))
    }
    // If only tag filter is active (no release filter), don't filter by showInBacklog
    // We'll keep all cards and let tag filtering determine if event should be outlined

    // Filter by tag card type - event must have cards matching tag filter
    // Only apply tag filtering if we have valid tag card type ID
    const hasValidTagCardType = tagCardTypeSet && tagCardTypeId

    if (hasValidTagCardType) {
      // Check if event has cards matching tag filter
      // IMPORTANT: Tag filtering checks ALL cards regardless of showInBacklog setting
      // Only the release filter considers showInBacklog
      let matchesTagCardType = false

      if (event.cards) {
        // Event must have at least one card with tagCardTypeId that has matching tags
        // Check all cards, not filtered by showInBacklog
        // Check both cardTypeId and cardType.id for compatibility
        matchesTagCardType = event.cards.some((card) => {
          const cardTypeId = card.cardTypeId || card.cardType?.id
          if (cardTypeId === tagCardTypeId) {
            const cardTags = getTagsForCard(card)
            return Array.isArray(cardTags) && cardTags.some((tag) => tagCardTypeSet.has(tag))
          }
          return false
        })
      }

      // If event doesn't match tag filter, remove all cards (event will be outlined)
      if (!matchesTagCardType) {
        filteredCards = []
      }
    }

    return {
      ...event,
      cards: filteredCards
    }
  })
}
